import { useMemo, useState } from "react";
import { GeoJSON, Marker, Polyline, Tooltip } from "react-leaflet";
import bezierSpline from "@turf/bezier-spline";
import { lineString } from "@turf/helpers";
import { icon } from "leaflet";
import hash from "object-hash";

import { Card, Stack, Typography } from "@mui/material";

import { MAP_CONTENTS_OPACITY } from "Configs/map";
import { ISurvey } from "Types/survey";

import { useAppSelector } from "Hooks/useRedux";
import useSurveys from "Hooks/useSurveys";

import DestinationIcon from "Assets/Images/Survey/destination-icon.svg";
import SourceIcon from "Assets/Images/Survey/source-icon.svg";

const ICON_SIZE = 22;

export default function Pointwise() {
  const { name } = useAppSelector((state) => state.operation);
  const { visible, markerMode } = useAppSelector((state) => state.surveys);
  const { visibleType, singleSurvayData } = useAppSelector((state) => state.singleLocation);

  const [hoveredItem, setHoveredItem] = useState<ISurvey | null>(null);

  const { data } = useSurveys();

  const surveyMarkers = useMemo(() => {
    if (!data && singleSurvayData.length === 0) return null;

    let finalData: ISurvey[] = [];
    if (data && visible) {
      finalData = data;
    } else if (visibleType === "survay") {
      finalData = singleSurvayData;
    }

    return finalData?.map((item) => {
      const sourceIcon = icon({ iconUrl: SourceIcon, iconSize: [ICON_SIZE, ICON_SIZE] });
      const destinationIcon = icon({ iconUrl: DestinationIcon, iconSize: [ICON_SIZE, ICON_SIZE] });

      const events = {
        mouseover: () => {
          setHoveredItem(item);
        },
        mouseout: () => {
          setHoveredItem(null);
        },
      };

      const finalResult = [];

      const latLng1 = [item.source_lat, item.source_lng];
      const latLng2 = [item.destination_lat, item.destination_lng];

      const offsetX = latLng2[1] - latLng1[1];
      const offsetY = latLng2[0] - latLng1[0];

      const r1 = Math.sqrt(offsetX ** 2 + offsetY ** 2);
      const theta = Math.atan2(offsetY, offsetX);

      const thetaOffset = 3.14 / 20;

      const r2 = r1 / 2 / Math.cos(thetaOffset);
      const theta2 = theta + thetaOffset;

      const midpointX = r2 * Math.cos(theta2) + latLng1[1];
      const midpointY = r2 * Math.sin(theta2) + latLng1[0];

      const midpointLatLng = [midpointY, midpointX];

      finalResult.push(latLng1, midpointLatLng, latLng2);

      const line = lineString(finalResult.map((latLng) => [latLng[1], latLng[0]]));
      const curved: any = bezierSpline(line, { sharpness: 1, resolution: 10000 });

      const isActive = name === "create-path" || name === "edit-path";

      const tooltipContent = !isActive && (
        <Tooltip direction="top" offset={[0, -20]}>
          <Card sx={{ py: 2, px: 3 }}>
            <Stack alignItems="center">
              <Typography>
                {item.first_name || "-"} {item.last_name || ""}
              </Typography>
              <Typography className="fa-number">{item.mobile}</Typography>
              <Typography className="fa-number">رسیدن: {item.go_time}</Typography>
              <Typography className="fa-number" sx={{ mb: "0 !important" }}>
                برگشت: {item.return_time}
              </Typography>
            </Stack>
          </Card>
        </Tooltip>
      );

      const opacity = isActive ? MAP_CONTENTS_OPACITY : 1;

      return (
        <>
          {markerMode.includes("source") || visibleType === "survay" ? (
            <Marker
              key={`source-${item.id}`}
              position={[item.source_lat, item.source_lng]}
              icon={sourceIcon}
              eventHandlers={events}
              opacity={opacity}
            >
              {tooltipContent}
            </Marker>
          ) : null}
          {markerMode.includes("destination") || visibleType === "survay" ? (
            <Marker
              key={`destination-${item.id}`}
              position={[item.destination_lat, item.destination_lng]}
              icon={destinationIcon}
              eventHandlers={events}
              opacity={opacity}
            >
              {tooltipContent}
            </Marker>
          ) : null}
          {((hoveredItem?.id === item.id && markerMode.length === 2) || (visibleType === "survay" && !visible)) && (
            <>
              <Polyline
                positions={[
                  [item.source_lat, item.source_lng],
                  [item.destination_lat, item.destination_lng],
                ]}
                pathOptions={{ opacity: 0.2, color: "#000", weight: 5 }}
                opacity={opacity}
              />
              <GeoJSON data={curved} pathOptions={{ weight: 5, color: "#0E1232", opacity }} key={hash(curved)} />
            </>
          )}
        </>
      );
    });
  }, [data, hoveredItem, name, markerMode, singleSurvayData, visibleType, visible]);

  if (!visible && !(visibleType === "survay")) return <></>;
  return <>{surveyMarkers}</>;
}
